/**
 * ProposalSystemOverview — Equipment summary
 *
 * Shows Solar, Battery, Generator sizes — only assets that exist in this scenario.
 * Renders as a horizontal row of icon+stat cards.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  WbSunny as SolarIcon,
  BatteryChargingFull as BatteryIcon,
  PowerSettingsNew as GeneratorIcon,
} from '@mui/icons-material';
import { vortxColors, vortxTypography, chartColors } from '@vortx/shared-ui';

const assets = [
  {
    key: 'pv',
    label: 'Solar',
    icon: SolarIcon,
    color: chartColors.solar,
    getValue: (sys) => sys.pvKw,
    format: (v) => `${Math.round(v)} kW`,
  },
  {
    key: 'storage',
    label: 'Battery',
    icon: BatteryIcon,
    color: chartColors.battery,
    getValue: (sys) => sys.storageKw,
    format: (v, sys) =>
      `${Math.round(v)} kW / ${Math.round(sys.storageKwh)} kWh`,
  },
  {
    key: 'generator',
    label: 'Generator',
    icon: GeneratorIcon,
    color: chartColors.generator,
    getValue: (sys) => sys.generatorKw,
    format: (v) => `${Math.round(v)} kW`,
  },
];

function AssetCards({ system }) {
  const activeAssets = assets.filter((a) => {
    const val = a.getValue(system);
    return val != null && val > 0;
  });

  if (activeAssets.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      {activeAssets.map((asset) => {
        const Icon = asset.icon;
        const val = asset.getValue(system);
        return (
          <Box
            key={asset.key}
            sx={{
              flex: '1 1 140px',
              minWidth: 140,
              p: 2,
              borderRadius: 2,
              border: `1px solid rgba(255,255,255,0.08)`,
              bgcolor: 'rgba(255,255,255,0.03)',
              textAlign: 'center',
            }}
          >
            <Icon sx={{ fontSize: 32, color: asset.color, mb: 0.5 }} />
            <Typography
              sx={{
                fontFamily: vortxTypography.mono,
                fontWeight: 700,
                fontSize: '1rem',
                color: vortxColors.white,
              }}
            >
              {asset.format(val, system)}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: vortxColors.stone }}
            >
              {asset.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

export default function ProposalSystemOverview({ system, hideTitle, entries }) {
  // Compare mode — multiple labeled groups
  if (entries) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {entries.map((entry, i) => (
          <Box key={i}>
            <Typography
              variant="body2"
              sx={{
                color: vortxColors.stone,
                fontWeight: 600,
                fontFamily: vortxTypography.body,
                fontSize: '0.8rem',
                mb: 1,
              }}
            >
              {entry.name}
            </Typography>
            <AssetCards system={entry.system} />
          </Box>
        ))}
      </Box>
    );
  }

  // Single scenario mode
  if (!system) return null;
  const hasAssets = assets.some((a) => {
    const val = a.getValue(system);
    return val != null && val > 0;
  });
  if (!hasAssets) return null;

  return (
    <Box>
      {!hideTitle && (
        <Typography
          variant="overline"
          sx={{
            color: vortxColors.stone,
            fontWeight: 600,
            letterSpacing: '0.1em',
            mb: 2,
            display: 'block',
          }}
        >
          Proposed System
        </Typography>
      )}
      <AssetCards system={system} />
    </Box>
  );
}
